import { RoutineType } from '../graphql';

// Tally counters are read-only during revision — editing a historical tally would
// corrupt cumulative counts for all later submissions.
const isTallyResponse = (item: any): boolean => item?.counterType === 'TALLY';

const SKIP_DIFF_KEYS = new Set([
  'i18n',
  'translations',
  '__typename',
  'exceptionCount',
]);

// For workflow response arrays: return the edited responses, but revert any
// tally questions back to their original values.
function getDataToSave(original: any[], edited: any[]): any[] {
  return edited.map((editedItem) => {
    if (!isTallyResponse(editedItem)) return editedItem;
    return (
      original.find((o) => o.questionId === editedItem.questionId) ?? editedItem
    );
  });
}

// Diff two workflow response arrays and return what changed per question.
function getResponseChanges(original: any[], edited: any[]) {
  const changes: {
    questionId: string;
    changes: Record<string, { oldValue: any; newValue: any }>;
  }[] = [];

  for (const originalItem of original) {
    if (isTallyResponse(originalItem)) continue;

    const editedItem = edited.find(
      (e) => e.questionId === originalItem.questionId
    );
    if (!editedItem) continue;

    const diff: Record<string, { oldValue: any; newValue: any }> = {};
    for (const key of new Set([
      ...Object.keys(originalItem),
      ...Object.keys(editedItem),
    ])) {
      if (SKIP_DIFF_KEYS.has(key)) continue;
      const oldVal = originalItem[key];
      const newVal = editedItem[key];
      const equal =
        Array.isArray(oldVal) && Array.isArray(newVal)
          ? JSON.stringify(oldVal) === JSON.stringify(newVal)
          : oldVal === newVal;
      if (!equal) diff[key] = { oldValue: oldVal, newValue: newVal };
    }

    if (Object.keys(diff).length > 0) {
      changes.push({ questionId: originalItem.questionId, changes: diff });
    }
  }

  return changes;
}

export const cleanUpDataChanges = (
  original: any,
  edited: any,
  routineType: RoutineType,
  userId: string
) => {
  let data: any;
  let auditLog: any[];

  const originalResponses = original?.responses;
  const editedResponses = edited?.responses;

  if (Array.isArray(originalResponses) && Array.isArray(editedResponses)) {
    // Workflow: full data objects containing a responses array
    data = {
      ...edited,
      responses: getDataToSave(originalResponses, editedResponses),
    };
    auditLog = getResponseChanges(originalResponses, editedResponses);
  } else if (routineType === RoutineType.Counter) {
    data = edited;
    auditLog =
      original?.count !== edited?.count
        ? [{ oldValue: original?.count, newValue: edited?.count }]
        : [];
  } else if (routineType === RoutineType.QuickConfirm) {
    data = edited;
    auditLog =
      original?.confirmedValue !== edited?.confirmedValue
        ? [
            {
              oldValue: original?.confirmedValue,
              newValue: edited?.confirmedValue,
            },
          ]
        : [];
  } else {
    data = edited;
    auditLog = [];
  }

  return {
    ...data,
    auditLog: { auditLog, userId },
  };
};
